import { useEffect, useState } from "react";
import { QrCode, RefreshCw, Printer, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

export default function AdminQRCodes() {
  const { selectedSite } = useAuth();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [printMode, setPrintMode] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get(`/admin/qrcodes?site=${selectedSite}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [selectedSite]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async (frontId) => {
    setError(""); setBusyId(frontId);
    try {
      await api.post(`/admin/fronts/${frontId}/qrcode`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const triggerPrint = (mode) => {
    setPrintMode(mode);
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 150);
  };

  const printRows = printMode === "all" ? rows : rows.filter((r) => r.Front_ID === printMode);

  return (
    <div className="space-y-4" data-testid="admin-qrcodes-panel">
      <div className="flex justify-between items-center flex-wrap gap-2 print:hidden">
        <div className="text-sm text-slate-400">
          QR codes stockés dans Google Drive (<span className="font-mono text-xs">/Configuration/QR_Fronts</span>). Chaque QR pointe vers la page de scan du front (choix inspection ou fiche).
        </div>
        <Button
          data-testid="admin-qrcodes-print-all-button"
          onClick={() => triggerPrint("all")}
          variant="outline"
          disabled={!rows.some((r) => r.qr?.QR_Drive_URL)}
          className="border-slate-700 text-xs"
        >
          <Printer className="w-4 h-4 mr-1.5" /> Imprimer tout (A4)
        </Button>
      </div>
      {error && <div data-testid="admin-qrcodes-error" className="print:hidden text-sm text-red-400">{error}</div>}
      <div className="grid sm:grid-cols-2 gap-3 print:hidden">
        {rows.map((f) => (
          <div key={f.Front_ID} data-testid={`admin-qrcode-row-${f.Front_ID}`} className="rounded-lg border border-slate-800 bg-[#121820] p-4 flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden">
              {f.qr?.QR_Drive_URL ? (
                <img data-testid={`admin-qrcode-image-${f.Front_ID}`} src={f.qr.QR_Drive_URL} alt={`QR ${f.Front_ID}`} className="w-full h-full object-contain" />
              ) : (
                <QrCode className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-100 truncate">{f.Front_Libelle}</div>
              <div className="text-xs text-slate-500 font-mono truncate">/scan/{f.Front_ID}</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button
                data-testid={`admin-qrcode-regenerate-${f.Front_ID}`}
                size="sm" variant="outline" disabled={busyId === f.Front_ID}
                onClick={() => regenerate(f.Front_ID)} className="border-slate-700 text-xs"
              >
                {busyId === f.Front_ID ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
              <Button
                data-testid={`admin-qrcode-print-${f.Front_ID}`}
                size="sm" variant="outline" disabled={!f.qr?.QR_Drive_URL}
                onClick={() => triggerPrint(f.Front_ID)} className="border-slate-700 text-xs"
              >
                <Printer className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {printMode && (
        <div className="hidden print:block">
          <div className={printMode === "all" ? "grid grid-cols-2 gap-6 p-6" : "flex flex-col items-center justify-center min-h-screen p-10"}>
            {printRows.map((f) => (
              <div key={f.Front_ID} className="flex flex-col items-center justify-center border border-black p-6 text-center break-inside-avoid">
                {f.qr?.QR_Drive_URL && <img src={f.qr.QR_Drive_URL} alt={f.Front_ID} className="w-48 h-48 object-contain mb-3" />}
                <div className="text-lg font-bold text-black">{f.Front_Libelle}</div>
                <div className="text-sm font-mono text-black">{f.Front_ID}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
